import {
  Environment,
  createMetrics,
  installBrowserErrorTracker,
} from '@moonpig/web-core-monitoring'
import NextApp, { AppContext as NextAppContext } from 'next/app'
import React, { ComponentProps, ReactNode } from 'react'
import { isServer } from '@moonpig/web-core-utils'
import { App } from './App'
import { handleRedirects, ServerRedirect } from './redirects/handleRedirects'

export type CreateAppArgs = {
  notifications: ReactNode[]
  inject?: ReactNode
  redirect?: ServerRedirect
}

export const createApp = ({ notifications, inject }: CreateAppArgs) => {
  const environment: Environment = {
    isLocalDev: process.env.NODE_ENV === 'development',
    environmentName: process.env.MNPG_ENVIRONMENT_NAME || 'local',
    trackJSAppName: process.env.TRACKJS_APP_NAME_DEFAULT || '',
    appVersion: process.env.APP_VERSION || '',
    trackJSEnableLocal: Boolean(process.env.TRACKJS_ENABLE_LOCAL),
    trackJSToken: process.env.TRACKJS_TOKEN || '',
    localHostName: 'localhost',
  }

  type NextAppAdapterProps = {
    pageProps: Omit<
      ComponentProps<typeof App>,
      'Component' | 'browserErrorTracker' | 'notifications' | 'inject'
    >
  }

  const browserErrorTracker = installBrowserErrorTracker(environment)

  class NextAppAdapter extends NextApp<NextAppAdapterProps> {
    public static async getInitialProps({
      Component,
      ctx,
    }: NextAppContext): Promise<NextAppAdapterProps> {
      const { res, req, query, pathname, asPath } = ctx

      const metrics = createMetrics()

      const customAsPath = isServer
        ? (req?.headers?.['Mnpg-Raw-Url'] as string) || ''
        : asPath || ''

      const redirectResult = await handleRedirects(customAsPath, res, metrics)
      if (redirectResult) return redirectResult

      const localHostName = (
        req ? req.headers.host || '' : window.location.host
      ).replace(/:\d+$/, '')

      const appInitialProps = await App.getInitialProps({
        ctx,
        route: {
          asPath: customAsPath,
          path: customAsPath.split(/\?/)[0],
          query,
          pathname,
          searchParams: customAsPath.split(/\?/)[1],
        },
        environment: { ...environment, localHostName },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Component: Component as any,
        browserErrorTracker,
        notifications,
      })

      if (res) {
        res.statusCode = appInitialProps.statusCode

        Object.entries(appInitialProps.headers).forEach(([name, value]) => {
          res.setHeader(name, value)
        })
      }

      const pageProps = {
        ...appInitialProps.props,
        ...((appInitialProps.statusCode === 301 ||
          appInitialProps.statusCode === 302) &&
        appInitialProps.headers?.location
          ? {
              location: appInitialProps.headers.location,
            }
          : {}),
      }

      return {
        pageProps,
      }
    }

    public componentDidCatch(error: Error) {
      browserErrorTracker.track(error)
    }

    public render() {
      const { pageProps, Component } = this.props

      return (
        <App
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          {...(pageProps as any)}
          Component={Component}
          inject={inject}
          browserErrorTracker={browserErrorTracker}
          notifications={notifications}
        />
      )
    }
  }

  return NextAppAdapter
}
